import React from "react";
import InitialPropsContext from "../lib/models/initialPropsContext";
import DownloadCenterPage, {
  Props as DownloadCenterProps,
  DOWNLOAD_TYPES
} from "../components/downloadCenter/DownloadCenterPage";
import _mapValues from "lodash/mapValues";
import PageProps from "../lib/models/pageProps";
import { sortByPositionField } from "../lib/sort";
import { Category } from "../lib/CmsApi";
import PageNotFoundError from "../lib/pageNotFoundError";

export default function JobOfferPage(props: DownloadCenterProps) {
  return <DownloadCenterPage {...props} />;
}

JobOfferPage.getInitialProps = async ({
  api,
  query
}: InitialPropsContext): PageProps<DownloadCenterProps> => {
  const { tab = DOWNLOAD_TYPES[0].id } = query;
  const [documents, clusters, seo] = await Promise.all([
    api
      .listDocuments({
        type: { path: "type", ne: "Programminformation" },
        limit: 10000,
        fields: ["file", "name", "type"],
        orderBy: "name",
        order: "asc"
      })
      .catch(() => ({ total: 0, items: [] })),
    api
      .listCategoryClusters({
        limit: 10000,
        fields: ["segment", "categories", "name", "posit"],
        orderBy: "posit"
      })
      .catch(() => ({ total: 0, items: [] })),

    api.loadDownloads().catch(() => {
      throw new PageNotFoundError();
    })
  ]);

  const guidelineDocs: DownloadCenterProps["guidelineDocs"] = [];
  let otherDocs: DownloadCenterProps["otherDocs"] = {};
  let programDocs: DownloadCenterProps["programDocs"] = {};

  documents.items.forEach(d => {
    const download = { id: d._id, title: d.name };
    if (d.type.type.toLowerCase() === "richtlinie") {
      guidelineDocs.push(download);
    } else {
      if (!otherDocs[d.type.name]) otherDocs[d.type.name] = [];
      otherDocs[d.type.name].push(download);
    }
  });

  otherDocs = Object.entries(otherDocs).reduce((acc, [key, entries]) => {
    if (
      [
        "Entsprechenserklärungen",
        "Jahresberichte",
        "Offenlegungsberichte"
      ].includes(key)
    ) {
      acc[key] = entries.reverse();
    } else {
      acc[key] = entries;
    }

    return acc;
  }, {});

  // Sort Programs into Segment -> Cluster -> Program -> DocumentType -> Documents structure
  clusters.items.forEach(cl => {
    // don't add clusters without a valid segment
    if (!cl.segment || typeof cl.segment.name === "undefined" || !cl.categories)
      return;
    // create segment group, if not existing yet
    if (!programDocs[cl.segment.name]) programDocs[cl.segment.name] = {};

    programDocs[cl.segment.name][cl.name] = {};

    const sortedCategories = sortByPositionField<Category>(cl.categories);

    sortedCategories.forEach(cat => {
      cat.programs &&
        (cat.programs as any).forEach(p => {
          // TODO remove any when api typing is fixed;
          if (p.documents) {
            programDocs[cl.segment.name][cl.name][p.name] = p.documents;
          }
        });
    });
  });

  // remove sections with no documents
  programDocs = _mapValues(programDocs, s => {
    Object.entries(s).forEach(([key, value]) => {
      if (!Object.values(value).length) {
        return delete s[key];
      }
      Object.entries(value).forEach(([key2, value2]) => {
        if (!value2.length) {
          return delete s[key][key2];
        }
      });
    });
    return s;
  });

  return {
    guidelineDocs,
    otherDocs,
    programDocs,
    activeTab: String(tab),
    ...seo
  };
};
